import React, {
  useState,
  useEffect,
  memo,
  useCallback,
  useLayoutEffect,
} from 'react';
import clsx from 'clsx';

import { Divider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CloseIcon, MenuIcon } from '../icons';
import { Text, View } from '../Base';
import palette from '../styles/palette';
import FilterSection from './FilterSection';

const useStyles = makeStyles(() => ({
  container: {
    width: '16.5rem',
    background: palette.slate,
    transform: 'translateX(16.5rem)',
    right: 0,
    top: 0,
    bottom: 0,
    display: 'flex',
    position: 'absolute',
    flexDirection: 'column',
    transition: 'transform .2s',
  },
  containerOpened: {
    transform: 'translateX(0)',
  },
  header: {
    fontSize: '1.375rem',
    lineHeight: '1.625rem',
    margin: '0.938rem 0 0.25rem',
    color: palette.typography.white.solid,
    paddingLeft: '1.25rem',
  },
  divider: {
    height: '0.063rem',
    background: palette.typography.white.ghost,
  },
  list: {
    paddingLeft: '1.25rem',
    overflowY: 'auto',
  },
  menuButton: {
    width: '2.125rem',
    height: '2.5rem',
    position: 'absolute',
    left: '-2.125rem',
    background: palette.slate,
    top: 'calc(50% - 1.25rem)',
    color: palette.typography.white.solid,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderTopLeftRadius: '0.313rem',
    borderBottomLeftRadius: '0.313rem',
    boxShadow: '0 0.25rem 0.25rem rgba(0, 0, 0, 0.25)',
    cursor: 'pointer',

    '& svg': {
      height: '1rem',
    },
  },
}));

const FilterPanel = ({
  filterConfig,
  hide,
  onChange,
  onClickFilterPanelButton,
  onLoad,
  selectedFilters,
  children,
  activeRoute,
}) => {
  const styles = useStyles();

  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    setOpen(false);
  }, [activeRoute]);

  useEffect(() => {
    open && onLoad && onLoad();
  }, [open, onLoad]);

  useEffect(() => {
    hide && setOpen(false);
  }, [hide]);

  const handleOnClickFilterButton = useCallback(() => {
    setOpen(!open);
    onClickFilterPanelButton && onClickFilterPanelButton(open);
  }, [open, onClickFilterPanelButton]);

  return (
    <View className={clsx(styles.container, open && styles.containerOpened)}>
      <View
        className={clsx(styles.menuButton, open && styles.closed)}
        onClick={handleOnClickFilterButton}
        data-testid="open-close-filter"
      >
        {open ? <CloseIcon /> : <MenuIcon />}
      </View>
      <Text className={styles.header}>{filterConfig.header}</Text>
      <Divider className={styles.divider} />
      <View className={styles.list}>
        {filterConfig.filters.map(({ title, field, values }, id) => (
          <FilterSection
            key={`${title}_${id}`}
            selectedValues={selectedFilters[field]}
            field={field}
            title={title}
            values={values}
            onChange={onChange}
          />
        ))}
      </View>
      {children}
    </View>
  );
};

export default memo(FilterPanel);
